import {
    Box,
    Button,
    Divider,
    FormLabel,
    Input,
    Select,
    Switch,
    Text,
    Textarea,
    VStack,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import PageCard from "../../../components/page-card";
import InlineErrorState from "../../../components/states/InlineErrorState";
import { translate } from "../../../i18n";
import { useGetUserMembershipTypes } from "../../user-management/hooks/useGetUserMembershipTypes";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateOrganizationDocumentVerification } from "../hooks/useUpdateOrganizationDocumentVerification";
import { useUpdateOrganizationSignupDocumentUpload } from "../hooks/useUpdateOrganizationSignupDocumentUpload";
import { isUnreadable } from '../../analytics/utils/statValue';

export default function SignupDocumentUploadSettingsCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const enabled = selectedOrganization?.signupDocumentUploadEnabled ?? false;
    // Missing on older API responses → true (uploads were always mandatory
    // before this flag existed).
    const required = selectedOrganization?.signupDocumentUploadRequired ?? true;
    const [title, setTitle] = useState(selectedOrganization?.signupDocumentUploadTitle ?? '');
    const [subtitle, setSubtitle] = useState(selectedOrganization?.signupDocumentUploadSubtitle ?? '');
    const [textsDirty, setTextsDirty] = useState(false);

    useEffect(() => {
        setTitle(selectedOrganization?.signupDocumentUploadTitle ?? '');
        setSubtitle(selectedOrganization?.signupDocumentUploadSubtitle ?? '');
        setTextsDirty(false);
    }, [selectedOrganization?.signupDocumentUploadTitle, selectedOrganization?.signupDocumentUploadSubtitle]);

    const { mutate, isLoading } = useUpdateOrganizationSignupDocumentUpload(
        selectedOrganization?.id || '',
        () => {
            toast({
                title: translate('organization.signup_document_upload_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
            setTextsDirty(false);
        },
    );

    const handleToggle = (nextEnabled: boolean) => {
        if (!selectedOrganization || isLoading) return;
        mutate({
            signupDocumentUploadEnabled: nextEnabled,
            signupDocumentUploadRequired: required,
            signupDocumentUploadTitle: title || null,
            signupDocumentUploadSubtitle: subtitle || null,
        }, {
            onError: () => {
                toast({
                    title: translate('error'),
                    status: 'error',
                    duration: 3000,
                    position: 'top-right',
                });
            },
        });
    };

    const handleToggleRequired = (nextRequired: boolean) => {
        if (!selectedOrganization || isLoading) return;
        mutate({
            signupDocumentUploadEnabled: enabled,
            signupDocumentUploadRequired: nextRequired,
            signupDocumentUploadTitle: title || null,
            signupDocumentUploadSubtitle: subtitle || null,
        }, {
            onError: () => {
                toast({
                    title: translate('error'),
                    status: 'error',
                    duration: 3000,
                    position: 'top-right',
                });
            },
        });
    };

    const handleSaveTexts = () => {
        if (!selectedOrganization || isLoading) return;
        mutate({
            signupDocumentUploadEnabled: enabled,
            signupDocumentUploadRequired: required,
            signupDocumentUploadTitle: title || null,
            signupDocumentUploadSubtitle: subtitle || null,
        }, {
            onError: () => {
                toast({
                    title: translate('error'),
                    status: 'error',
                    duration: 3000,
                    position: 'top-right',
                });
            },
        });
    };

    if (!selectedOrganization) return null;

    return (
        <PageCard title={translate('organization.signup_document_upload_title')}>
            <Box p={4}>
                <Box display="flex" alignItems="center" gap={3} mb={2}>
                    <Switch
                        isChecked={enabled}
                        isDisabled={isLoading}
                        onChange={(e) => handleToggle(e.target.checked)}
                        colorScheme="brand"
                        aria-label={translate('organization.signup_document_upload_title')}
                    />
                    <FormLabel mb={0} color={textColorPrimary}>
                        {translate('organization.signup_document_upload_title')}
                    </FormLabel>
                </Box>
                <Text fontSize="sm" color="gray.500" mb={4}>
                    {translate('organization.signup_document_upload_helper')}
                </Text>

                {enabled && (
                    <VStack spacing={3} align="stretch">
                        <Box>
                            <Box display="flex" alignItems="center" gap={3} mb={1}>
                                <Switch
                                    isChecked={required}
                                    isDisabled={isLoading}
                                    onChange={(e) => handleToggleRequired(e.target.checked)}
                                    colorScheme="brand"
                                    aria-label={translate('organization.signup_document_upload_required_label')}
                                />
                                <FormLabel mb={0} color={textColorPrimary}>
                                    {translate('organization.signup_document_upload_required_label')}
                                </FormLabel>
                            </Box>
                            <Text fontSize="sm" color="gray.500">
                                {translate('organization.signup_document_upload_required_helper')}
                            </Text>
                        </Box>
                        <Box>
                            <FormLabel fontSize="sm" color={textColorPrimary}>
                                {translate('organization.signup_document_upload_title_label')}
                            </FormLabel>
                            <Input
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    setTextsDirty(true);
                                }}
                                placeholder={translate('organization.signup_document_upload_title_placeholder')}
                                maxLength={256}
                            />
                        </Box>
                        <Box>
                            <FormLabel fontSize="sm" color={textColorPrimary}>
                                {translate('organization.signup_document_upload_subtitle_label')}
                            </FormLabel>
                            <Input
                                value={subtitle}
                                onChange={(e) => {
                                    setSubtitle(e.target.value);
                                    setTextsDirty(true);
                                }}
                                placeholder={translate('organization.signup_document_upload_subtitle_placeholder')}
                                maxLength={512}
                            />
                        </Box>
                        {textsDirty && (
                            <Button
                                size="sm"
                                colorScheme="brand"
                                isLoading={isLoading}
                                onClick={handleSaveTexts}
                                alignSelf="flex-end"
                            >
                                {translate('organization.signup_document_upload_save_texts')}
                            </Button>
                        )}

                        <Divider my={2} />
                        <DocumentVerificationSection />
                    </VStack>
                )}
            </Box>
        </PageCard>
    );
}

// Nested under the upload settings because verification is meaningless without
// uploads enabled. Renders only when the parent toggle is on.
function DocumentVerificationSection() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const orgId = selectedOrganization?.id || '';
    /**
     * The membership type chosen here is what a VERIFIED document grants, so an
     * empty chooser is not a cosmetic gap: it reads as "this organization has
     * no membership types to grant", on the screen where document verification
     * is being switched on. A refused or failed read leaves `data` undefined
     * and `(membershipTypes ?? []).map(...)` renders a Select with nothing but
     * its placeholder — while `documentVerificationMembershipTypeId` may
     * already hold a perfectly good id that now matches no option, so the
     * control also stops showing the operator their own current setting.
     */
    const { data: membershipTypes, isError: isMembershipTypesError } =
        useGetUserMembershipTypes(orgId);

    const [enabled, setEnabled] = useState(
        selectedOrganization?.documentVerificationEnabled ?? false,
    );
    const [membershipTypeId, setMembershipTypeId] = useState<string>(
        selectedOrganization?.documentVerificationMembershipTypeId ?? '',
    );
    const [customPrompt, setCustomPrompt] = useState<string>(
        selectedOrganization?.documentVerificationCustomPrompt ?? '',
    );
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setEnabled(selectedOrganization?.documentVerificationEnabled ?? false);
        setMembershipTypeId(
            selectedOrganization?.documentVerificationMembershipTypeId ?? '',
        );
        setCustomPrompt(selectedOrganization?.documentVerificationCustomPrompt ?? '');
        setDirty(false);
    }, [
        selectedOrganization?.documentVerificationEnabled,
        selectedOrganization?.documentVerificationMembershipTypeId,
        selectedOrganization?.documentVerificationCustomPrompt,
    ]);

    const { mutate, isLoading } = useUpdateOrganizationDocumentVerification(
        orgId,
        () => {
            toast({
                title: translate('organization.document_verification_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
            setDirty(false);
        },
    );

    const handleSave = () => {
        if (!selectedOrganization || isLoading) return;
        if (enabled && !membershipTypeId) {
            toast({
                title: translate('organization.document_verification_missing_membership'),
                status: 'error',
                duration: 3000,
                position: 'top-right',
            });
            return;
        }
        mutate(
            {
                documentVerificationEnabled: enabled,
                documentVerificationMembershipTypeId: membershipTypeId || null,
                documentVerificationCustomPrompt: customPrompt || null,
            },
            {
                onError: () => {
                    toast({
                        title: 'Failed to save',
                        status: 'error',
                        duration: 3000,
                        position: 'top-right',
                    });
                },
            },
        );
    };

    return (
        <VStack spacing={3} align="stretch">
            <Text fontSize="md" fontWeight="bold" color={textColor}>
                {translate('organization.document_verification_section_title')}
            </Text>
            <Text fontSize="sm" color="gray.500">
                {translate('organization.document_verification_section_helper')}
            </Text>

            <Box display="flex" alignItems="center" gap={3}>
                <Switch
                    isChecked={enabled}
                    isDisabled={isLoading}
                    onChange={(e) => {
                        setEnabled(e.target.checked);
                        setDirty(true);
                    }}
                    colorScheme="brand"
                />
                <FormLabel mb={0} color={textColor}>
                    {translate('organization.document_verification_toggle_label')}
                </FormLabel>
            </Box>

            {enabled && (
                <>
                    <Box>
                        <FormLabel fontSize="sm" color={textColor}>
                            {translate('organization.document_verification_membership_label')}
                        </FormLabel>
                        {/* `isUnreadable`, not the raw flag: React Query KEEPS `membershipTypes` through
                            a failed background refetch, and this arm REPLACES the control. Reading
                            the flag alone took a working membership-type select
                            away from the operator because a refresh was refused
                            (useGetUserMembershipTypes, staleTime 0 — R132-1). */}
                        {isUnreadable(isMembershipTypesError, membershipTypes) ? (
                            <InlineErrorState message={translate('userMembership.error_loading')} />
                        ) : (
                            <Select
                                placeholder={translate('organization.document_verification_membership_placeholder')}
                                value={membershipTypeId}
                                onChange={(e) => {
                                    setMembershipTypeId(e.target.value);
                                    setDirty(true);
                                }}
                            >
                                {(membershipTypes ?? []).map((mt) => (
                                    <option key={mt.id} value={mt.id}>
                                        {mt.name}
                                        {mt.priceInCents !== undefined && mt.priceInCents !== null && mt.priceInCents > 0
                                            ? ` — paid (admin review only)`
                                            : ''}
                                    </option>
                                ))}
                            </Select>
                        )}
                    </Box>
                    <Box>
                        <FormLabel fontSize="sm" color={textColor}>
                            {translate('organization.document_verification_custom_prompt_label')}
                        </FormLabel>
                        <Textarea
                            value={customPrompt}
                            onChange={(e) => {
                                setCustomPrompt(e.target.value);
                                setDirty(true);
                            }}
                            placeholder={translate('organization.document_verification_custom_prompt_placeholder')}
                            maxLength={4000}
                            rows={3}
                        />
                    </Box>
                </>
            )}

            {dirty && (
                <Button
                    size="sm"
                    colorScheme="brand"
                    isLoading={isLoading}
                    onClick={handleSave}
                    alignSelf="flex-end"
                >
                    {translate('organization.document_verification_save')}
                </Button>
            )}
        </VStack>
    );
}
